import { z } from 'zod';
import { zIsoDate, zNonEmpty, zUuid } from '@saas/shared';

/**
 * knowledge の DTO と入力スキーマ（03 §14〜§16）。サーバ（internal/）とブラウザ（ui/）の両方がここを見る。
 * Date は JSON では ISO 文字列で届く。
 */

// ---- 語彙 ----

/**
 * 断片の源泉（§14.8 source_type）。段 4 で受けるもの + 本人のメモ + 記事 + 添付ファイル。
 * ★ `storage_file` は段 5 の追加（§16.2 段 4）: storage のフォルダに置かれたファイル。
 *   添付（`file`。core の fileId 基準）と混ざらないよう別の種類にしている（gap「storage の取込に要ること」④）
 * ★ `email` は SFA 段階 1B の追加: crm の共有メール記録（crm.email.shared）。本文は crm から取り直し、2 段目は判定器 crm_email_record
 */
export const SOURCE_TYPES = ['report', 'workflow', 'contract', 'deal', 'conversation', 'meeting', 'wiki', 'file', 'storage_file', 'note', 'article', 'email'] as const;
export type SourceType = (typeof SOURCE_TYPES)[number];

/** 権限メタ（§14.8 visibility）。restricted は「インデックスしない」なので断片には存在しない */
export const VISIBILITIES = ['public', 'department', 'owner', 'participants'] as const;
export type Visibility = (typeof VISIBILITIES)[number];

/** 受理判定の拒否リスト（§14.9b・§14.13.3）。source_subtype がこれに当たれば knowledge 側でも二重に弾く */
export const DENYLIST_SUBTYPES = ['dm', 'channel', 'anonymous_survey', 'safety_response', 'hr_ringi', 'contract_restricted', 'employment_contract', 'restricted', 'system_post'] as const;
export type DenylistSubtype = (typeof DENYLIST_SUBTYPES)[number];

export const INGEST_PRIORITIES = ['high', 'medium', 'low'] as const;
export type IngestPriority = (typeof INGEST_PRIORITIES)[number];

export const SOURCE_STATUSES = ['queued', 'processing', 'indexed', 'rejected', 'failed'] as const;
export type SourceStatus = (typeof SOURCE_STATUSES)[number];

/** 記事の状態機械（§14.14.4）。published に入る矢印は人の操作からしか出ない */
export const ARTICLE_STATUSES = ['draft', 'proposed', 'published', 'stale', 'rejected', 'archived'] as const;
export type ArticleStatus = (typeof ARTICLE_STATUSES)[number];
export const ARTICLE_KINDS = ['faq', 'howto', 'decision_rule', 'case'] as const;
export type ArticleKind = (typeof ARTICLE_KINDS)[number];

export const NOTE_INPUT_METHODS = ['typed', 'voice', 'after_call', 'after_meeting'] as const;
export type NoteInputMethod = (typeof NOTE_INPUT_METHODS)[number];
export const SHARE_SCOPES = ['private', 'department', 'org'] as const;
export type ShareScope = (typeof SHARE_SCOPES)[number];

export const QA_STATUSES = ['candidate', 'proposed', 'accepted', 'rejected'] as const;
export type QaStatus = (typeof QA_STATUSES)[number];

/** person スコープの重み（§15.6）: 本人のメモ 3.0 / ピン留め 2.0 / 稟議の承認コメント 1.5 / その他 1.0 */
export const PERSON_SCOPE_WEIGHT = { note: 3.0, pinned: 2.0, workflow: 1.5, default: 1.0 } as const;

/** 出典の日付にこの日数以上経っていれば警告を付ける（§14.9c「古いものには警告」） */
export const STALE_AGE_DAYS = 365;
/** 記事の棚卸し（§14.14.3）: 最終確認から 12 か月 */
export const ARTICLE_REVIEW_MONTHS = 12;
/** エキスパート検索・棚卸しを出さない期間（§14.11: 契約から 6 か月） */
export const EXPERT_READY_MONTHS = 6;
/** 退職者の引き出しの付与期限の既定（§15.5） */
export const PERSONA_ACCESS_DEFAULT_MONTHS = 12;

/** 通知の kind（title / body を直接渡す。テンプレートは使わない） */
export const NOTIFICATION_KIND_PROPOSAL = 'knowledge.article.proposed';
export const NOTIFICATION_KIND_STALE = 'knowledge.article.stale';
export const NOTIFICATION_KIND_HANDOVER = 'knowledge.handover.needed';
export const NOTIFICATION_KIND_INGEST_FAILED = 'knowledge.ingest.failed';

// ---- 出典（§14.9c: 出典と日付を必ず付ける） ----

export interface Citation {
  chunkId: string;
  /** 既存の種類（SourceType）か、提供者の種類（10 §4.2・例 `customer`）。表示は sourceLabel を使う */
  sourceType: string;
  sourceId: string;
  /** 「稟議」「日報」「会議」…（提供者の label → 既存の表示名 → 種類名） */
  sourceLabel: string;
  title: string;
  /** 記録の状態（提供者の status・例「未確定」「下書き」。10 §4.2）。無ければ null */
  status?: string | null;
  /** 該当箇所の抜粋（検索語を含む前後） */
  excerpt: string;
  /** 元の出来事の日時（鮮度の判定に使う） */
  occurredAt: Date;
  ageDays: number;
  /** 1 年以上前の記録 */
  stale: boolean;
  /** 元レコードの画面。knowledge が知らない種類は null（fileId があればファイルの URL API で開く） */
  link: string | null;
  fileId: string | null;
  locator: Record<string, unknown> | null;
  contributors: Array<{ id: string; name: string }>;
  /** person スコープでの重み（org スコープでは 1.0 固定で返す） */
  weight: number;
  /**
   * 同系統のファイル（§16.6）。代表だけを出し「同じ系統のファイルが N 件あります」と添える。
   * ★ 「これが最新です」とは断定しない・古いものは消さない
   */
  group?: { versionGroupId: string; isLatest: boolean; count: number } | null;
}

export type SearchScope = 'org' | 'person';

export interface SearchResult {
  queryId: string;
  scope: SearchScope;
  /** person スコープの対象者 */
  subjectUser: { id: string; name: string } | null;
  /** 見出し（person スコープは「◯◯さんの足跡から」。「◯◯さんに聞ける」とは言わない・§15.3） */
  heading: string;
  /** AI の回答（AnswerProvider が居ないときは null = 検索結果の抜粋だけ返す） */
  answer: string | null;
  answeredBy: 'ai' | 'excerpts' | 'none';
  /** ★ 段 5: 回答生成がプランで止まっている（pro のみ・01 §4.1）。検索と抜粋はそのまま出る */
  answerLocked?: boolean;
  citations: Citation[];
  /** 「1 件の記録に基づく回答です」「◯◯さんの記録にはありません」など */
  notices: string[];
  /** person スコープの免責（§15.3） */
  disclaimer: string | null;
  /** 埋め込み検索が有効だったか（無ければ全文検索だけ） */
  vectorUsed: boolean;
  /** 検索に使った語（日本語の分かち書きのあと・10 §4.6）。画面の「◯◯ で探しました」 */
  terms?: string[];
}

/** AI の回答に渡す断片の数と 1 件の長さ（10 §4.6「上位 8 件の断片の全文・各 800 字まで」）。回答の [n] は結果の n 番目 */
export const ANSWER_SOURCE_LIMIT = 8;
export const ANSWER_SOURCE_MAX_CHARS = 800;

/** データ量が要る画面の使える時期（契約から 6 か月・プラン）。ExpertSearchResult / HandoverView と同じ意味 */
export interface KnowledgeScreenReadiness {
  ready: boolean;
  /** 使えるようになる日（YYYY-MM-DD）。プランで止まっているときは null */
  availableFrom: string | null;
  planLocked: boolean;
}

/**
 * 記録がたまっているか（`GET /knowledge/readiness`・10 §5）。件数は出さない（読めない記録の量を見せない）。
 *   hasRecords … 探せる記録が 1 件でもあるか。false なら検索画面に「まだ探せる記録がありません」と取り込み状況への案内
 *   qa / proposals / experts / handover … データ量が要る画面。使えるまでは開いたときに「記録がたまると使えます」と説明する（メニューからは外さない）
 */
export interface KnowledgeReadiness {
  hasRecords: boolean;
  /** Q&A の候補が 1 件でもある（週次の抽出がまだ何も拾っていなければ false） */
  qa: boolean;
  /** ナレッジ化の提案が 1 件でもある（見送り・下書きにしたものを含む） */
  proposals: boolean;
  experts: KnowledgeScreenReadiness;
  handover: KnowledgeScreenReadiness;
}

export interface Suggestion extends Citation {
  /** なぜ出したか（same_record / same_customer / same_deal / same_project / same_contract） */
  reason: string;
}

// ---- 個人スコープ（§15） ----

export interface NoteSummary {
  id: string;
  authorId: string;
  body: string;
  inputMethod: NoteInputMethod;
  refType: string | null;
  refId: string | null;
  customerId: string | null;
  topicIds: string[];
  shareScope: ShareScope;
  pinned: boolean;
  referencedCount: number;
  createdAt: Date;
  updatedAt: Date | null;
}

export interface FootprintItem {
  chunkId: string;
  sourceType: SourceType;
  sourceId: string;
  sourceLabel: string;
  title: string;
  excerpt: string;
  occurredAt: Date;
  link: string | null;
  /** 本人が「これは共有しない」にした */
  optedOut: boolean;
  /** 本人が「これは自分の代表的な知識」にした */
  pinned: boolean;
}

export interface PersonaAccessLogItem {
  id: string;
  viewerId: string;
  viewerName: string;
  query: string | null;
  resultCount: number | null;
  createdAt: Date;
}

export interface PersonaSettings {
  userId: string;
  /** private = 誰にも公開しない / department = 同じ部署と直属の上長（既定）/ org = 全社 */
  shareScope: ShareScope;
  /** ★ 段 5: オンボーディングのメンター（§15.7）。決まっていれば「探す」の既定の範囲をこの人の足跡にする */
  mentorUserId?: string | null;
  mentorName?: string | null;
}

export interface PersonaGrant {
  id: string;
  subjectUserId: string;
  granteeId: string;
  granteeName: string;
  reason: 'handover' | 'management' | 'onboarding';
  expiresOn: string | null;
  grantedBy: string | null;
  grantedAt: Date;
}

// ---- 記事・Q&A・トピック（§14.14） ----

export interface ArticleSummary {
  id: string;
  kind: ArticleKind;
  title: string;
  question: string | null;
  status: ArticleStatus;
  topicIds: string[];
  ownerId: string | null;
  ownerName: string | null;
  visibility: 'public' | 'department' | 'owner';
  departmentId: string | null;
  helpfulCount: number;
  unhelpfulCount: number;
  needsReview: boolean;
  staleReason: string | null;
  /** ★ 段 5: 陳腐化の詳しい理由（矛盾した数字・重なっている記事の題名など）。§14.14.3 */
  staleDetail?: string | null;
  /** ★ 段 5: wiki に昇格したページ（§14.4a）。記事が陳腐化したら wiki 側にも印を付ける */
  wikiPageId?: string | null;
  proposalReason: string | null;
  publishedAt: Date | null;
  lastReviewedAt: Date | null;
  reviewDueOn: string | null;
  createdAt: Date;
  updatedAt: Date;
}

export interface ArticleDetail extends ArticleSummary {
  body: string;
  sourceChunkIds: string[];
  /** 元断片（出典として表示） */
  sources: Citation[];
  confirmedBy: string | null;
  confirmedAt: Date | null;
  actions: { edit: boolean; publish: boolean; reject: boolean; archive: boolean; review: boolean };
}

export interface QaCandidate {
  id: string;
  question: string;
  answer: string | null;
  occurrences: number;
  status: QaStatus;
  articleId: string | null;
  firstSeenAt: Date;
  lastSeenAt: Date;
  sourceChunkIds: string[];
}

export interface TopicSummary {
  id: string;
  name: string;
  aliases: string[];
  ownerUserId: string | null;
  chunkCount: number;
  autoGenerated: boolean;
}

// ---- エキスパート検索・棚卸し（§14.6・§14.7） ----

export interface ExpertEvidence {
  meetings: number;
  reports: number;
  conversations: number;
  approvals: number;
  opened: number;
  notes: number;
  total: number;
  /** 直近 3 か月の件数 */
  recent: number;
}

export interface Expert {
  userId: string;
  name: string;
  departmentId: string | null;
  departmentName: string | null;
  score: number;
  evidence: ExpertEvidence;
  lastActivityAt: Date | null;
  presence: { state: string; activity: string | null } | null;
}

export interface ExpertSearchResult {
  /** 契約から 6 か月未満は false（§14.11）。experts は空 */
  ready: boolean;
  /** ★ 段 5: プランで止まっている（pro のみ・01 §4.1）。画面は「プロ プランで使えます」を出す */
  planLocked?: boolean;
  availableFrom: string | null;
  message: string | null;
  query: string;
  topics: TopicSummary[];
  experts: Expert[];
  articles: ArticleSummary[];
}

export interface SoleHeldTopic {
  topicId: string;
  topicName: string;
  score: number;
  chunkCount: number;
  /** 次点の人のスコア比（0〜1）。0 = 完全に単独 */
  runnerUpRatio: number;
  runnerUpUserId: string | null;
}

export interface HandoverItemSummary {
  id: string;
  /** ★ 段 5: customer / project を足した（§14.7・gap A10）。旧 3 種はそのまま */
  kind: 'topic' | 'article' | 'note' | 'customer' | 'project';
  refId: string | null;
  title: string;
  detail: string | null;
  successorId: string | null;
  successorName?: string | null;
  action: 'document' | 'train' | 'transfer' | null;
  status: 'open' | 'done';
  /** 引き継ぎのやり方（人が書く） */
  note?: string | null;
  doneAt?: Date | null;
}

export interface HandoverSummary {
  id: string;
  leavingUserId: string;
  reason: 'resign' | 'transfer' | 'leave' | 'other';
  effectiveOn: string | null;
  status: 'draft' | 'submitted' | 'done';
  workflowRequestId: string | null;
  workflowRequestNo: string | null;
  items: HandoverItemSummary[];
  createdAt: Date;
}

export interface HandoverView {
  ready: boolean;
  /** ★ 段 5: プランで止まっている（pro のみ・01 §4.1） */
  planLocked?: boolean;
  availableFrom: string | null;
  message: string | null;
  user: { id: string; name: string; departmentId: string | null; status: string };
  soleHeldTopics: SoleHeldTopic[];
  topics: Array<{ topicId: string; topicName: string; score: number; chunkCount: number; isSoleHolder: boolean }>;
  articles: ArticleSummary[];
  sharedNoteCount: number;
  sourceCounts: Record<string, number>;
  handover: HandoverSummary | null;
  /**
   * （SFA §15.3.2・任意）営業の移管対象: その人が担当の進行中の見込み客・商談・未完了の追客カードの数と、sales の引き継ぎ画面
   * （`/sales/handovers?userId=`）。sales が居る・会社で有効なときだけ（無ければ省く）。件数は権限で絞らない。cards は task が無い会社で null
   */
  salesSummary?: { leads: number; deals: number; cards: number | null; url: string };
}

// ---- 取込の状態（管理・§14.13.10） ----

export interface IngestSourceSummary {
  id: string;
  /** 既存の種類（SourceType）か、提供者の種類（10 §4.2・英小文字の種類名）。表示名は sourceTypeLabel() で引く */
  sourceType: string;
  sourceId: string;
  sourceSubtype: string | null;
  eventName: string | null;
  status: SourceStatus;
  reason: string | null;
  priority: IngestPriority;
  attempts: number;
  lastError: string | null;
  title: string | null;
  chunkCount: number;
  occurredAt: Date;
  indexedAt: Date | null;
  createdAt: Date;
  updatedAt: Date;
}

export interface IngestStats {
  byStatus: Record<SourceStatus, number>;
  bySourceType: Record<string, number>;
  chunkCount: number;
  articleCount: number;
  noteCount: number;
  /** 埋め込み列（pgvector）があるか・埋め込みプロバイダが居るか */
  vector: { column: boolean; provider: boolean };
  /**
   * gap-knowledge 2（追加・任意）: ANN（ベクトル検索）から外している古い断片の数（02 §4.1b）。
   * 全文検索・出典の表示には残っている。日次ジョブ knowledge.archive_cold が付け外しする
   */
  annArchivedChunks?: number;
}

// ---- 入力（Zod） ----

export const zSearchQuery = z
  .object({
    q: zNonEmpty.max(300),
    scope: z.enum(['org', 'person']).default('org'),
    /** scope = person の対象者。省略時は自分 */
    userId: zUuid.optional(),
    limit: z.coerce.number().int().min(1).max(20).default(10),
    // ---- 絞り込み（10_ナレッジ全データ化設計書 §4.6・§5）----
    /** 種類（複数）。`types=report,meeting` でも配列でもよい。種類名の形（英小文字・数字・_）だけ受ける */
    types: z
      .preprocess(
        (v) => (typeof v === 'string' ? v.split(',').map((s) => s.trim()).filter(Boolean) : v),
        z.array(z.string().regex(/^[a-z][a-z0-9_]{1,39}$/)).max(30),
      )
      .optional(),
    /** 取引先（その取引先に関わる記録） */
    customerId: zUuid.optional(),
    /** 記録の日付（YYYY-MM-DD・会社の時間帯の暦日。to はその日を含む）。2 月 31 日のような存在しない日は弾く（DB で落とさない） */
    from: zIsoDate.refine((s) => new Date(`${s}T00:00:00Z`).toISOString().slice(0, 10) === s, { message: '存在しない日付です' }).optional(),
    to: zIsoDate.refine((s) => new Date(`${s}T00:00:00Z`).toISOString().slice(0, 10) === s, { message: '存在しない日付です' }).optional(),
    /** 関わった人（担当・作成・参加）。本人以外は「◯◯さんの足跡から」と同じ公開の範囲で引け、引いたことは本人に見える（§15.4 ④・§15.5） */
    personId: zUuid.optional(),
  })
  .refine((v) => !v.from || !v.to || v.from <= v.to, { message: '期間の始まりが終わりより後になっています', path: ['to'] });
export type SearchQueryInput = z.infer<typeof zSearchQuery>;

export const zSuggestQuery = z.object({
  subjectType: z.string().regex(/^[a-z][a-z0-9_]*$/),
  subjectId: zUuid,
  limit: z.coerce.number().int().min(1).max(10).default(5),
});
export type SuggestQueryInput = z.infer<typeof zSuggestQuery>;

export const zQueryOpened = z.object({ chunkId: zUuid });
export const zQueryFeedback = z.object({ helpful: z.boolean() });

/** 「◯◯さんの足跡から」（scope = person 固定。対象者は URL で受ける） */
export const zPersonSearchQuery = z.object({ q: zNonEmpty.max(300), limit: z.coerce.number().int().min(1).max(20).default(10) });
export type PersonSearchQuery = z.infer<typeof zPersonSearchQuery>;

export const NOTE_BODY_MAX = 2000;
export const zCreateNote = z.object({
  body: zNonEmpty.max(NOTE_BODY_MAX),
  inputMethod: z.enum(NOTE_INPUT_METHODS).default('typed'),
  refType: z.string().regex(/^[a-z][a-z0-9_]*$/).nullable().optional(),
  refId: zUuid.nullable().optional(),
  customerId: zUuid.nullable().optional(),
  shareScope: z.enum(SHARE_SCOPES).default('department'),
  pinned: z.boolean().default(false),
});
export type CreateNoteInput = z.infer<typeof zCreateNote>;

export const zUpdateNote = z.object({
  body: zNonEmpty.max(NOTE_BODY_MAX).optional(),
  shareScope: z.enum(SHARE_SCOPES).optional(),
  pinned: z.boolean().optional(),
});
export type UpdateNoteInput = z.infer<typeof zUpdateNote>;

export const zPersonaSettings = z.object({ shareScope: z.enum(SHARE_SCOPES) });

/** 足跡の個別設定（§15.4 ②） */
export const zOptout = z.object({ optedOut: z.boolean() });
export const zPinFootprint = z.object({ pinned: z.boolean() });

export const zCreateGrant = z.object({
  granteeId: zUuid,
  reason: z.enum(['handover', 'management', 'onboarding']).default('handover'),
  /** 省略時は 12 か月後（§15.5） */
  expiresOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
});
export type CreateGrantInput = z.infer<typeof zCreateGrant>;

export const ARTICLE_BODY_MAX = 20000;
export const zCreateArticle = z.object({
  kind: z.enum(ARTICLE_KINDS).default('howto'),
  title: zNonEmpty.max(200),
  question: z.string().trim().max(500).nullable().optional(),
  body: z.string().max(ARTICLE_BODY_MAX).default(''),
  topicIds: z.array(zUuid).max(10).default([]),
  visibility: z.enum(['public', 'department', 'owner']).default('public'),
  departmentId: zUuid.nullable().optional(),
  sourceChunkIds: z.array(zUuid).max(50).default([]),
});
export type CreateArticleInput = z.infer<typeof zCreateArticle>;

export const zUpdateArticle = zCreateArticle.partial().extend({ ownerId: zUuid.nullable().optional() });
export type UpdateArticleInput = z.infer<typeof zUpdateArticle>;

export const zArticleFeedback = z.object({ helpful: z.boolean() });

/** 記事の状態を進める（§14.14.4 の状態機械。許される矢印はサービス側で見る） */
export const zArticleTransition = z.object({ status: z.enum(ARTICLE_STATUSES) });
export type ArticleTransitionInput = z.infer<typeof zArticleTransition>;

export const zArticleListQuery = z.object({
  status: z.enum(ARTICLE_STATUSES).optional(),
  topicId: zUuid.optional(),
  q: z.string().trim().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  cursor: z.string().optional(),
});
export type ArticleListQuery = z.infer<typeof zArticleListQuery>;

export const zTopicInput = z.object({
  name: zNonEmpty.max(100),
  aliases: z.array(zNonEmpty.max(100)).max(20).default([]),
  ownerUserId: zUuid.nullable().optional(),
});
export type TopicInput = z.infer<typeof zTopicInput>;

export const zQaListQuery = z.object({
  status: z.enum(QA_STATUSES).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});

export const zExpertQuery = z.object({ q: zNonEmpty.max(200), limit: z.coerce.number().int().min(1).max(10).default(5) });

export const zCreateHandover = z.object({
  reason: z.enum(['resign', 'transfer', 'leave', 'other']).default('resign'),
  effectiveOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
  successorId: zUuid.nullable().optional(),
  /** true なら workflow に引き継ぎ計画を起票する（workflow が無効なら下書きのまま） */
  submit: z.boolean().default(false),
});
export type CreateHandoverInput = z.infer<typeof zCreateHandover>;

export const zSourceListQuery = z.object({
  status: z.enum(SOURCE_STATUSES).optional(),
  /** 既存の種類か提供者の種類（10 §4.3: 種類は固定の一覧ではなく種類名の形で確かめる） */
  sourceType: z.string().regex(/^[a-z][a-z0-9_]{1,39}$/).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  cursor: z.string().optional(),
});
export type SourceListQuery = z.infer<typeof zSourceListQuery>;

export const zFootprintQuery = z.object({
  limit: z.coerce.number().int().min(1).max(200).default(50),
  cursor: z.string().optional(),
});

// ---- 出典の表示（サーバとブラウザで共通） ----

export const SOURCE_LABEL: Record<SourceType, string> = {
  report: '日報',
  workflow: '稟議',
  contract: '契約',
  deal: '商談',
  conversation: '会話',
  meeting: '会議',
  wiki: 'マニュアル',
  file: 'ファイル',
  storage_file: 'ファイル',
  note: 'メモ',
  article: '記事',
  email: '共有メール',
};

/** 2 段目の判定器に聞く subject の種類（storage が登録する・§16.8） */
export const STORAGE_FILE_SUBJECT_TYPE = 'storage_file';

/**
 * 元レコードの画面。conversation の会話ページと同じ「段 4 時点のルートの写し」（knowledge は他モジュールの画面を知らない）。
 * locator に conversationId があれば会話ページ（?message= で投稿へスクロール）。無い種類は null
 */
export function sourceLink(sourceType: SourceType, sourceId: string, locator?: Record<string, unknown> | null): string | null {
  switch (sourceType) {
    case 'report':
      return `/report/reports/${sourceId}`;
    case 'workflow':
      return `/workflow/requests/${sourceId}`;
    case 'contract':
      return `/contract/contracts/${sourceId}`;
    case 'deal':
      return `/sales/deals/${sourceId}`;
    case 'conversation': {
      const conv = locator && typeof locator.conversationId === 'string' ? locator.conversationId : null;
      return conv ? `/conversation/${conv}?message=${sourceId}` : null;
    }
    case 'meeting':
      return `/meeting-ingest/meetings/${sourceId}`;
    case 'wiki':
      return `/wiki/pages/${sourceId}`;
    case 'article':
      return `/knowledge/articles/${sourceId}`;
    case 'note':
      return '/knowledge/notes';
    // SFA 1B: 共有メールは関連先の画面（取引先 → 見込み客 → 商談）。本文はその画面の共有メールから開く
    case 'email': {
      const pick = (k: string): string | null => (locator && typeof locator[k] === 'string' ? (locator[k] as string) : null);
      const customerId = pick('customerId');
      if (customerId) return `/crm/customers/${customerId}`;
      const leadId = pick('leadId');
      if (leadId) return `/sales/leads/${leadId}`;
      const dealId = pick('dealId');
      return dealId ? `/sales/deals/${dealId}` : null;
    }
    // ★ §16.9: ファイル由来の出典にも「元の場所」を出す。storage のファイルは storage の画面、
    //   添付は添付元のレコードの画面（locator.parentType / parentId は normalizeFile が入れる）
    case 'storage_file':
      return `/storage/files/${sourceId}`;
    case 'file': {
      const parentType = locator && typeof locator.parentType === 'string' ? (locator.parentType as SourceType) : null;
      const parentId = locator && typeof locator.parentId === 'string' ? locator.parentId : null;
      if (!parentType || !parentId || parentType === 'file' || parentType === 'storage_file') return null;
      return sourceLink(parentType, parentId, locator);
    }
  }
}

/**
 * 該当箇所の言い方（§16.9 の［該当箇所を開く］のラベル）。位置が分からなければ null。
 * ★ PDF は署名 URL に `#page=N` を付けて開く（ページ番号はここから取る）
 */
export function locatorLabel(locator: Record<string, unknown> | null | undefined): string | null {
  if (!locator) return null;
  if (typeof locator.page === 'number' && locator.page > 0) return `${locator.page} ページ目`;
  if (typeof locator.slide === 'number' && locator.slide > 0) return `スライド ${locator.slide}`;
  if (typeof locator.sheet === 'string' && locator.sheet) return `シート「${locator.sheet}」`;
  if (typeof locator.heading === 'string' && locator.heading) return `「${locator.heading}」`;
  return null;
}

/** PDF のページ指定（署名 URL の後ろに付ける）。ページが分からなければ空文字 */
export function pageFragment(locator: Record<string, unknown> | null | undefined): string {
  const page = locator && typeof locator.page === 'number' ? locator.page : 0;
  return page > 0 ? `#page=${page}` : '';
}

export function ageDaysOf(occurredAt: Date, now: Date = new Date()): number {
  return Math.max(0, Math.floor((now.getTime() - occurredAt.getTime()) / 86_400_000));
}

/** 「◯◯さんの足跡から」（§15.3 の呼び方。「◯◯さん AI」「◯◯さんに聞く」は使わない） */
export function personScopeHeading(name: string): string {
  return `${name}さんの足跡から`;
}

export function personScopeDisclaimer(name: string): string {
  return `これは${name}さんの記録を検索した結果で、${name}さん本人の見解ではありません。記録にないことは答えられません。`;
}

// ===========================================================================
// 段 5: storage のフォルダの取込（§16.2 段 4・§16.7・§16.8）。ここから下は**追加だけ**
// ===========================================================================

/** フォルダの取込設定（§16.7「導入時に 10 分で決められること」） */
export const FOLDER_RULE_MODES = ['include', 'exclude'] as const;
export type FolderRuleMode = (typeof FOLDER_RULE_MODES)[number];

/** 規則が無いフォルダの既定: 直近 24 か月に更新 or 直近 12 か月に開かれたものだけ入れる（§16.7） */
export const FOLDER_DEFAULT_UPDATED_MONTHS = 24;
export const FOLDER_DEFAULT_OPENED_MONTHS = 12;

/** 取り込まなかった理由（`knowledge_file_texts.skip_reason`。管理画面に日本語で出す） */
export const STORAGE_SKIP_REASONS = ['personal', 'folder_excluded', 'folder_name', 'unsupported_ext', 'snapshot_image', 'duplicate_hash', 'not_recent', 'plan', 'spreadsheet', 'too_large', 'no_extractor', 'no_text_layer', 'encrypted', 'unreadable', 'empty', 'ocr_unavailable', 'ocr_timeout', 'ocr_failed', 'ocr_page_limit', 'pdf_page_limit', 'pdf_text_limit'] as const;
export type StorageSkipReason = (typeof STORAGE_SKIP_REASONS)[number];

export const SKIP_REASON_LABEL: Record<string, string> = {
  personal: '個人フォルダのファイルです',
  folder_excluded: 'このフォルダは「取り込まない」に設定されています',
  folder_name: 'バックアップ・一時・ゴミ箱などのフォルダです',
  unsupported_ext: '本文を読めない形式です',
  snapshot_image: '写真・スクリーンショットです（3MB 未満）',
  duplicate_hash: '同じ中身のファイルが既に入っています',
  not_recent: '2 年以上更新がなく、1 年以上開かれていません',
  plan: 'フォルダの取込はプロプランの機能です',
  spreadsheet: '表計算はシート名だけを入れています（個別の金額は AI では答えません）',
  too_large: 'ファイルが大きすぎます（20MB まで）',
  no_extractor: '本文を読み取れませんでした',
  no_text_layer: 'スキャン画像の PDF です（文字が入っていません）',
  ocr_unavailable: 'OCR 実行環境がありません（サーバーの設定を確認してください）',
  ocr_timeout: 'OCR の制限時間を超えました。ページを分割してお試しください',
  ocr_failed: 'OCR で読み取れませんでした',
  ocr_page_limit: 'OCR 対象のページ数が上限を超えています。PDF を分割してください',
  pdf_page_limit: 'PDF のページ数が上限を超えています（300 ページまで）',
  pdf_text_limit: 'PDF の文字数が上限を超えています。PDF を分割してください',
  encrypted: '暗号化された PDF です',
  unreadable: 'ファイルが壊れているか、読み取れません',
  empty: '本文がありません',
};

export function skipReasonText(reason: string | null | undefined): string {
  if (!reason) return '—';
  return SKIP_REASON_LABEL[reason] ?? reason;
}

/** Selection exclusions (personal folders, etc.) cannot be overridden by an OCR retry. */
export const EXTRACTION_RETRY_REASONS = ['no_text_layer', 'no_extractor', 'unreadable', 'empty', 'ocr_unavailable', 'ocr_timeout', 'ocr_failed', 'ocr_page_limit', 'pdf_page_limit', 'pdf_text_limit'] as const;
export interface FileExtractionSummary {
  id: string;
  filename: string;
  status: 'pending' | 'done' | 'failed' | 'skipped';
  method: string;
  reason: string | null;
  pageCount: number | null;
  retryable: boolean;
}

export interface FolderRuleSummary {
  id: string;
  folderId: string;
  folderPath: string;
  mode: FolderRuleMode;
  recursive: boolean;
  setBy: string | null;
  setByName: string | null;
  setAt: Date;
}

/** 管理画面「フォルダの取込設定」の 1 行（`StoragePublicApi.listFolderTree` の木に ON / OFF / 継承を付けたもの） */
export interface FolderTreeNode {
  id: string;
  parentId: string | null;
  name: string;
  path: string;
  fileCount: number;
  /** このフォルダ自身に付いた規則（無ければ null） */
  rule: FolderRuleMode | null;
  recursive: boolean;
  /** 実際にどう扱われるか。default = 規則が無い（直近に使われたものだけ入る） */
  effective: FolderRuleMode | 'default';
  /** 継承元のパス（自分自身に規則があれば null） */
  inheritedFrom: string | null;
}

export interface FolderRulesView {
  /** storage が有効で、プランが pro なら true */
  available: boolean;
  /** 使えない理由（日本語） */
  reason: string | null;
  nodes: FolderTreeNode[];
}

export const zSetFolderRule = z.object({
  folderId: zUuid,
  folderPath: zNonEmpty.max(1000),
  /** null = 規則を外す（既定に戻す） */
  mode: z.enum(FOLDER_RULE_MODES).nullable(),
  recursive: z.boolean().default(true),
});
export type SetFolderRuleInput = z.infer<typeof zSetFolderRule>;

/** 初回の一括取込（§16.11「夜間にレート制御しながら」）の進み具合 */
export interface StorageBackfillStatus {
  running: boolean;
  /** 次に読む fileEntryId（null = 先頭から / 完了） */
  cursor: string | null;
  scanned: number;
  indexed: number;
  skipped: number;
  startedAt: Date | null;
  finishedAt: Date | null;
  lastError: string | null;
}

/** 管理画面「取り込んだ量と、検索で実際に開かれた割合」（§16.7・gap A15） */
export interface IngestCoverage {
  storage: {
    available: boolean;
    reason: string | null;
    /** 断片になった storage のファイル数 */
    indexed: number;
    skipped: number;
    /** 理由ごとの件数（skip_reason → 件数） */
    bySkipReason: Record<string, number>;
    backfill: StorageBackfillStatus | null;
  };
  /** 直近 90 日の検索。opened = 出典を 1 つ以上開いた検索の数 */
  usage: { queries: number; opened: number; openedRatio: number; helpful: number; unhelpful: number };
}

/** 一括の再投入（gap A24）。failed / rejected をまとめて queued に戻す */
export const zRequeueSources = z.object({
  status: z.enum(['failed', 'rejected']).default('failed'),
  limit: z.coerce.number().int().min(1).max(500).default(100),
});
export type RequeueSourcesInput = z.infer<typeof zRequeueSources>;

/** 同系統ファイル（§16.6・gap A14）。検索では代表だけ出し「同じ系統のファイルが N 件あります」 */
export interface FileGroupInfo {
  versionGroupId: string;
  isLatest: boolean;
  /** 同じ系統のファイル数（自分を含む） */
  count: number;
}

/** 「最終」「修正」「v2」「コピー」「(2)」を落として、同系統のファイル名をひとまとめにする（§16.6） */
export function normalizeFileBaseName(name: string): string {
  const withoutExt = name.replace(/\.[A-Za-z0-9]{1,8}$/, '');
  return (
    withoutExt
      .normalize('NFKC')
      .toLowerCase()
      // 「(2)」「（3）」と、日付（2026-04-01 / 20260401）は区切りを潰す前に落とす
      .replace(/[（(]\s*\d+\s*[）)]/g, ' ')
      .replace(/(?:20\d{2}|19\d{2})[-_/.]?\d{1,2}[-_/.]?\d{1,2}/g, ' ')
      // 区切り（空白・アンダースコア・ハイフン・中黒）をすべて空白にしてから語を落とす
      .replace(/[\s_\-–—.·・]+/g, ' ')
      .replace(/(?<![a-z0-9])(?:v|ver|version|rev)\s*\d+(?:\.\d+)*(?![a-z])/g, ' ')
      .replace(/\d{6,8}/g, ' ')
      .replace(/(?:最終版?|最新版?|確定版?|修正版?|改訂版?|訂正版?|コピー|写し|旧版?|新版?|差替え?|差し替え?|バックアップ|copy|final|latest|fix|draft|old|new|backup)/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
  );
}

// ===========================================================================
// 段 5（その 2・引き出す側）: 文脈サジェスト・会議後の 1 問・提案・棚卸し・プラン。
// ここから下も **追加だけ**（既存の型・関数の意味は変えない）
// ===========================================================================

// ---- プラン（01 §4.1・gap core A2）----

/**
 * pro でだけ使える機能（01 §4.1）。standard は「検索（抜粋の表示）」と「個人スコープ（部署内）」まで。
 *   aiAnswer     … 全社横断の AI 検索（回答生成）。standard は抜粋だけ返す（検索そのものは止めない）
 *   expertSearch … エキスパート検索（詳しい人を探す）
 *   handover     … 知識の棚卸し・引き継ぎ・引き出しの期間つき付与
 *   folderIngest … storage のフォルダの取込（その 1 で実装済み）。★ 2026-10-03（10 §4.5）から全プランで既定で取り込む（capabilitiesFor は常に true）。
 *                  型と proOnlyReason は互換のため残す
 */
export const KNOWLEDGE_PRO_FEATURES = ['aiAnswer', 'expertSearch', 'handover', 'folderIngest'] as const;
export type KnowledgeProFeature = (typeof KNOWLEDGE_PRO_FEATURES)[number];

export const PRO_ONLY_MESSAGE = 'プロ プランで使えます';

export interface KnowledgeCapabilities {
  plan: string;
  /** pro だけの機能が使えるか。false のときは画面に「プロ プランで使えます」を出す */
  aiAnswer: boolean;
  expertSearch: boolean;
  handover: boolean;
  folderIngest: boolean;
}

export function proOnlyReason(feature: KnowledgeProFeature): string {
  switch (feature) {
    case 'aiAnswer':
      return `社内の記録からの AI の回答は${PRO_ONLY_MESSAGE}。抜粋と出典はプランに関わらず表示されます。`;
    case 'expertSearch':
      return `詳しい人を探す機能は${PRO_ONLY_MESSAGE}。`;
    case 'handover':
      return `知識の棚卸しは${PRO_ONLY_MESSAGE}。`;
    case 'folderIngest':
      return `フォルダの取込は${PRO_ONLY_MESSAGE}。`;
  }
}

// ---- 文脈サジェスト（§14.5・gap A3）----

/** どの差し込み口から出したか（ヒット率の分母を分ける・§16.7） */
export const ASSIST_SURFACES = ['form.assist', 'record.panel'] as const;
export type AssistSurface = (typeof ASSIST_SURFACES)[number];

/** 入力が止まってからこの時間だけ待って引く（打つたびに引かない） */
export const ASSIST_DEBOUNCE_MS = 800;
/** 起票フォームの横に出す件数（§14.5「3 件まで」） */
export const ASSIST_FORM_LIMIT = 3;
/** レコード画面の「このレコードについて社内が知っていること」 */
export const ASSIST_RECORD_LIMIT = 5;
/** これより短い入力では引かない（打ち始めのノイズを出さない） */
export const ASSIST_MIN_TEXT = 6;

export const zAssistQuery = z
  .object({
    surface: z.enum(ASSIST_SURFACES).default('record.panel'),
    /** レコードに紐づくとき（record.panel / 起票元が決まっている form.assist） */
    subjectType: z.string().regex(/^[a-z][a-z0-9_]*$/).optional(),
    subjectId: zUuid.optional(),
    /** 入力中の題名 + 本文（form.assist）。空なら subject だけで引く */
    text: z.string().trim().max(2000).optional(),
    customerId: zUuid.optional(),
    limit: z.coerce.number().int().min(1).max(10).default(3),
  })
  .refine((v) => !!v.text || (!!v.subjectType && !!v.subjectId) || !!v.customerId, { message: '入力中の本文かレコードのどちらかが要ります' });
export type AssistQueryInput = z.infer<typeof zAssistQuery>;

/**
 * 文脈サジェストの結果。`queryId` は「出した」記録（knowledge_queries）で、
 * 開かれた → `POST /knowledge/queries/:id/opened`、閉じられた → `POST /knowledge/queries/:id/dismissed`
 */
export interface AssistResult {
  queryId: string | null;
  surface: AssistSurface;
  suggestions: Suggestion[];
  /** 「探す」への導線に使う検索語（レコード名・入力中の題名から作る）。無ければ null */
  searchTerm: string | null;
}

// ---- 会議後の 1 問（§15.2 ①・§15.9 A・gap A4）----

export const AFTER_MEETING_QUESTION = '今の要点を一言だけ';
export const AFTER_MEETING_HELP = '書かなくても構いません。書けば、次に同じ相手と話す人の助けになります。';
export const AFTER_MEETING_MUTE_LABEL = 'この顧客の会議では今後聞かない';

export interface AfterMeetingPrompt {
  /** 聞いてよいか（「今後聞かない」にされていれば false） */
  ask: boolean;
  /** 既に同じ会議でメモを書いていれば、その ID（二重に聞かない） */
  existingNoteId: string | null;
  question: string;
  help: string;
  /** 「今後聞かない」を出せるか（顧客が分かっているときだけ） */
  canMute: boolean;
}

export const zAfterMeetingQuery = z.object({ meetingId: zUuid, customerId: zUuid.optional() });
export const zAfterMeetingMute = z.object({ customerId: zUuid });

// ---- ナレッジ化の提案（§14.14.2・gap A7 / C3）----

export const PROPOSAL_STATUSES = ['open', 'accepted', 'dismissed', 'never'] as const;
export type ProposalStatus = (typeof PROPOSAL_STATUSES)[number];

export interface ProposalSummary {
  id: string;
  reason: 'qa' | 'similar_cases';
  title: string;
  question: string | null;
  summary: string | null;
  topicIds: string[];
  occurrences: number;
  status: ProposalStatus;
  targetUserId: string | null;
  targetUserName: string | null;
  articleId: string | null;
  /** 元になった記録（出典と日付つき。これを見てから［下書きを作る］を押す） */
  sources: Citation[];
  createdAt: Date;
  decidedAt: Date | null;
}

export const zProposalListQuery = z.object({
  status: z.enum(PROPOSAL_STATUSES).optional(),
  /** 自分に来ている提案だけ（既定は自分 + 担当のないもの） */
  mine: z.coerce.boolean().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(20),
});
export type ProposalListQuery = z.infer<typeof zProposalListQuery>;

export const NOTIFICATION_KIND_PROPOSAL_OFFER = 'knowledge.article.proposal';
export const NOTIFICATION_KIND_STALE_DIGEST = 'knowledge.article.stale_digest';
export const NOTIFICATION_KIND_CONFLICT = 'knowledge.article.conflict';
export const NOTIFICATION_KIND_NOTE_USED = 'knowledge.note.referenced';
export const NOTIFICATION_KIND_SOLE_HELD = 'knowledge.sole_held.monthly';
export const NOTIFICATION_KIND_GRANT_EXPIRING = 'knowledge.persona.expiring';

// ---- 陳腐化の 5 条件（§14.14.3・gap A8 / C2）----

/**
 * 陳腐化の理由。★ conflict（矛盾）だけは即時に通知し、ほかは月初の 1 通にまとめる。
 *   source_changed … 元にした断片が消えた / 作り直された
 *   not_reviewed   … 最終確認から 12 か月
 *   ref_ended      … 参照元（契約の終了・案件の完了）から 90 日 → アーカイブ候補
 *   duplicate      … 90% 以上重なる記事がある → 統合の提案
 *   conflict       … 記事の中の金額・日付・割合と、同じ参照先の新しい記録の値が食い違う。
 *                    gap-knowledge 1 で「文章として食い違う」（LLM の判定・同じ話題の新しい記録）も同じ理由で立つ
 */
export const STALE_REASONS = ['source_changed', 'not_reviewed', 'ref_ended', 'duplicate', 'conflict'] as const;
export type StaleReason = (typeof STALE_REASONS)[number];

export const STALE_REASON_LABEL: Record<string, string> = {
  source_changed: '元にした記録が更新・削除されました',
  not_reviewed: `最終確認から ${ARTICLE_REVIEW_MONTHS} か月が経ちました`,
  ref_ended: '参照していた契約・案件が終わってから 90 日が経ちました（アーカイブの候補です）',
  duplicate: 'ほとんど同じ内容の記事があります（統合を検討してください）',
  conflict: '新しい記録と数字・内容が食い違っています',
};

export function staleReasonText(reason: string | null | undefined): string {
  if (!reason) return '';
  return STALE_REASON_LABEL[reason] ?? reason;
}

/**
 * 矛盾フラグの文章版（LLM・gap-knowledge 1）の根拠 1 件。`GET /knowledge/articles/:id/conflicts`。
 * ★ 要点（recordClaim / reason）は記録の本文から作った文なので、**その記録を読める人にだけ**返す（2 段判定）
 */
export interface ArticleConflictFinding {
  id: string;
  checkedAt: Date;
  /** 記事側の要点 */
  articleClaim: string | null;
  /** 記録側の要点 */
  recordClaim: string | null;
  /** なぜ両立しないか（AI の説明） */
  reason: string | null;
  /** 根拠の記録（出典と日付つき） */
  source: Citation;
}

export interface ArticleConflictView {
  findings: ArticleConflictFinding[];
  /** 読めない・もう消えた記録の件数（中身は出さない） */
  hiddenCount: number;
}

/** 参照元が終わってからアーカイブ候補にするまでの日数（§14.14.3） */
export const REF_ENDED_ARCHIVE_DAYS = 90;
/** 記事の統合を提案する重なり（§14.14.3「90% 以上重複」） */
export const ARTICLE_DUPLICATE_THRESHOLD = 0.9;

// ---- wiki への昇格（§14.4a・gap A9）----

export const zPromoteToWiki = z.object({ spaceId: zUuid, parentId: zUuid.nullable().optional() });
export type PromoteToWikiInput = z.infer<typeof zPromoteToWiki>;

export interface WikiPromotion {
  /** wiki が無効 / 組み込まれていなければ false（画面はボタンを出さない） */
  available: boolean;
  wikiPageId: string | null;
  link: string | null;
}

// ---- 棚卸しの項目（§14.7・gap A10）----

export const HANDOVER_ITEM_KINDS = ['topic', 'article', 'note', 'customer', 'project'] as const;
export type HandoverItemKind = (typeof HANDOVER_ITEM_KINDS)[number];

export const HANDOVER_ITEM_KIND_LABEL: Record<string, string> = {
  topic: 'トピック',
  article: '記事',
  note: 'メモ',
  customer: '担当している顧客',
  project: '担当している案件',
};

export const zUpdateHandoverItem = z.object({
  successorId: zUuid.nullable().optional(),
  action: z.enum(['document', 'train', 'transfer']).nullable().optional(),
  status: z.enum(['open', 'done']).optional(),
  note: z.string().trim().max(1000).nullable().optional(),
});
export type UpdateHandoverItemInput = z.infer<typeof zUpdateHandoverItem>;

/** ★ §14.6: エキスパート検索・棚卸しの画面に必ず出す（人を評価する道具ではない） */
export const EXPERT_NOT_FOR_HR = '人事評価に使用しないでください。記録の量は仕事の価値ではありません。';

// ---- オンボーディング（§15.7・gap A22）----

export const zMentorSetting = z.object({ mentorUserId: zUuid.nullable() });

// ---- 記事一覧の CSV（03 第 I 部 §7 のチェックリスト・gap B2）----

export function csvCell(v: unknown): string {
  const s = v === null || v === undefined ? '' : String(v);
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export const ARTICLE_CSV_HEADER = ['ID', '種類', '題名', '質問', '状態', '担当', '公開範囲', '役に立った', '役に立たなかった', '陳腐化の理由', '公開日', '最終確認日', '次の確認期限'] as const;

/** 記事一覧の CSV（BOM 付き UTF-8 は呼び出し側で付ける）。日付は YYYY-MM-DD */
export function articlesToCsv(rows: readonly ArticleSummary[]): string {
  const date = (d: Date | string | null): string => (d ? new Date(d).toISOString().slice(0, 10) : '');
  const lines = [ARTICLE_CSV_HEADER.join(',')];
  for (const a of rows) {
    lines.push(
      [a.id, a.kind, a.title, a.question ?? '', a.status, a.ownerName ?? '', a.visibility, a.helpfulCount, a.unhelpfulCount, staleReasonText(a.staleReason), date(a.publishedAt), date(a.lastReviewedAt), a.reviewDueOn ?? '']
        .map(csvCell)
        .join(','),
    );
  }
  return lines.join('\r\n');
}

// ===========================================================================
// ナレッジ全データ化（10_ナレッジ全データ化設計書 §4.3・§5・2026-10-03・kd-sync）。ここから下も**追加だけ**
//   提供者（KnowledgeSourceProvider）の種類は固定の一覧ではなく文字列で扱う（SourceType は既存の種類のまま残す）
// ===========================================================================

/** 種類の一覧（`GET /knowledge/source-types`）。kd-search の絞り込みと表示名に使う。**この名前と形を変えない** */
export interface KnowledgeSourceTypeView {
  sourceType: string;
  label: string;
}

/** 提供者の無い既存の種類（本人のメモ・記事・storage のファイル・添付）。提供者の種類と一緒に一覧に出す */
export const LEGACY_SOURCE_TYPES = ['note', 'article', 'storage_file', 'file'] as const;

/** 種類名の形（カーネルの登録簿と同じ規則）。URL の `:sourceType` もこれで確かめる */
export const SOURCE_TYPE_NAME_RE = /^[a-z][a-z0-9_]{1,39}$/;
export const zSourceTypeName = z.string().regex(SOURCE_TYPE_NAME_RE);

/** 表示名: 提供者の label → 既存の表示名（SOURCE_LABEL）→ 種類名 の順 */
export function sourceTypeLabel(sourceType: string, providerLabel?: string | null): string {
  if (providerLabel && providerLabel.trim()) return providerLabel.trim();
  return (SOURCE_LABEL as Record<string, string>)[sourceType] ?? sourceType;
}

/** 種類の並び（検索の絞り込みの印・取り込み状況の表）。仕事の流れに近い順で、ここに無い種類は後ろに種類名の順 */
export const SOURCE_TYPE_ORDER: readonly string[] = [
  'customer', 'contact', 'activity', 'email', 'lead', 'inquiry', 'deal', 'quote',
  'meeting', 'calendar_event', 'telephony_call', 'conversation', 'task_card', 'project',
  'contract', 'workflow', 'report', 'expense', 'invoice', 'campaign', 'survey',
  'wiki', 'docmost_page', 'note', 'article', 'storage_file', 'file',
];

export function compareSourceTypes(a: string, b: string): number {
  const rank = (t: string): number => {
    const i = SOURCE_TYPE_ORDER.indexOf(t);
    return i < 0 ? SOURCE_TYPE_ORDER.length : i;
  };
  return rank(a) - rank(b) || a.localeCompare(b);
}

/** 同期の状態（knowledge_sync_state.status）。idle = 追いついている / backfilling = 遡及の途中 / error = 3 回続けて失敗 / disabled = 止めている */
export const SYNC_STATUSES = ['idle', 'backfilling', 'error', 'disabled'] as const;
export type KnowledgeSyncStatus = (typeof SYNC_STATUSES)[number];

export const SYNC_STATUS_LABEL: Record<KnowledgeSyncStatus, string> = {
  idle: '最新',
  backfilling: '遡及の途中',
  error: '失敗が続いています',
  disabled: '止めています',
};

/** 種類ごとの同期の状態（管理画面の「取り込み状況」） */
export interface KnowledgeSyncStateView {
  status: KnowledgeSyncStatus;
  /** 会社の設定（機能フラグ `knowledge.source_<種類>`）。false なら止めていて、その会社の源泉は消える */
  enabledByTenant: boolean;
  providerVersion: number | null;
  lastRunAt: Date | null;
  lastSuccessAt: Date | null;
  lastError: string | null;
  lastErrorAt: Date | null;
  consecutiveFailures: number;
  backfillStartedAt: Date | null;
  /** 遡及（先頭からの全件の読み取り）が終わった時刻。null = 途中か、まだ始まっていない */
  backfillCompletedAt: Date | null;
  /** 週 1 回の掃除（物理削除の取りこぼしの追随）が動いているか・最後に終わった時刻 */
  sweepRunning: boolean;
  sweepCompletedAt: Date | null;
  /** 「読み直す」を押してまだ始まっていない */
  resyncRequested: boolean;
  /** 累計（読んだ・取り込んだ・消した・除外した） */
  counts: { read: number; indexed: number; deleted: number; excluded: number };
}

/** 種類ごとの取り込み状況（`GET /knowledge/admin/coverage` の types） */
export interface KnowledgeSourceCoverage {
  sourceType: string;
  label: string;
  /** 提供者がある（5 分ごとの同期で取り込む）。false = イベント・表から取り込む既存の種類 */
  provider: boolean;
  /** 台帳（knowledge_sources）の行数と状態ごとの件数 */
  sources: number;
  indexed: number;
  /** 待ち（queued / processing） */
  pending: number;
  failed: number;
  /** 入れなかった（除外リスト・判定器が無い・本文が無い など）。理由ごとの件数は byReason */
  excluded: number;
  byReason: Record<string, number>;
  /** 断片の数（添付の断片も親の種類に数える） */
  chunks: number;
  /** 提供者の種類だけ。まだ一度も同期していなければ null */
  sync: KnowledgeSyncStateView | null;
}

/**
 * 管理画面「取り込み状況」（`GET /knowledge/admin/coverage`）。
 * ★ 既存の `IngestCoverage`（storage の取り込んだ量・検索で開かれた割合）を**そのまま含む**ので、フォルダの取込設定の画面は変えずに動く
 */
export interface KnowledgeCoverageView extends IngestCoverage {
  types: KnowledgeSourceCoverage[];
  /** 同期の設定（環境変数 KNOWLEDGE_SYNC_INTERVAL_MIN / KNOWLEDGE_SYNC_BATCH） */
  sync: { intervalMinutes: number; batch: number };
}

/** 「読み直す」の結果 */
export interface KnowledgeResyncResult {
  /** 読み直しを予約した種類（次の同期でカーソルを先頭に戻して全件を読む。本文と権限が同じものは何もしない） */
  sourceTypes: string[];
}

/** 同期で除外した理由（knowledge_sources.reason）の日本語。既存の理由は画面側の一覧（reasonText）で引く */
export function syncReasonText(reason: string | null | undefined): string | null {
  if (!reason) return null;
  if (reason.startsWith('no_resolver:')) return `権限の判定の仕組み（${reason.slice('no_resolver:'.length)}）がまだ無いので、誰の検索にも出せません`;
  if (reason.startsWith('invalid_item:')) return `提供者から届いた記録の形が正しくありません（${reason.slice('invalid_item:'.length)}）`;
  if (reason === 'removed_by_admin') return '管理者が検索から外しました（記録が更新されたら取り込み直します）';
  return null;
}
